import './ProjectDetail.css';
import {
  ProjectFullWidth,
  ProjectSideBySide,
  ProjectSideBySideReverse,
  ProjectFooter
} from './ProjectCommon';
import projects from './projects';

function DishaProjectDetail() {
  // Get project data from projects.js
  const projectData = projects.find(p => p.id === 'disha');

  const footerProps = {
    title: "Project Disha",
    description: projectData ? projectData.description : "",
    collaborators: [
      {
        name: "Mitul Iyengar",
        url: "https://www.linkedin.com/in/mituliyengar/"
      },
      {
        name: "Siddhi Patil",
        url: "https://www.linkedin.com/in/patilsiddhi/"
      }
    ],
    backgroundImage: "/disha/footer.webp"
  };

  return (
    <div className="project-detail-root">
      <div className="project-detail-hero">
        <img src="/disha/hero.webp" alt="Project Disha Hero" className="project-detail-hero-img" />
      </div>
      <div className="project-detail-content">
        <div className="project-detail-intro">
          <div className="project-detail-title">
            <h1>Project Disha</h1>
            <p className="project-credit">
              A project by Luke Fiorante, <a href="https://www.linkedin.com/in/mituliyengar/" target="_blank" rel="noopener noreferrer" className="project-link">Mitul Iyengar</a>, and <a href="https://www.linkedin.com/in/patilsiddhi/" target="_blank" rel="noopener noreferrer" className="project-link">Siddhi Patil</a>
            </p>
          </div>
          <p>
            <b>Project Disha</b> is a <b>systemic design initiative</b> addressing the crisis of preventable, early-stage hysterectomies among migrant sugarcane harvesters in Maharashtra, India. Rather than prescribing a single, top-down solution to this complex problem, our work focused on <b>empowering local stakeholders</b>. Our primary deliverable is <b>"Preventing Hysterectomies: A Field Guide for Systemic Action,"</b> a manual designed to help community leaders and organizations understand the intricate system at play and develop their own context-specific interventions.
          </p>
        </div>
        <div className="project-detail-process">
          <ProjectFullWidth image="/disha/exhibition.webp" alt="Project Disha Exhibition" caption="An exhibition for Project Disha.">
            <h2>Problem Definition</h2>
            <p>
              Annually, nearly 1.5 million people in Maharashtra migrate for the sugarcane harvest, enduring grueling 16-hour workdays and poor living conditions. Within this system, women laborers face immense pressure to work without interruption. Menstruation is often viewed as a barrier to income, leading to a tragically high rate of hysterectomies in women as young as their 20s and 30s. These major surgeries are often performed without medical necessity and can introduce new health complications without resolving the underlying issues of pain or exhaustion.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/disha/systems_mapping.webp" alt="Overview of the systemic factors contributing to early hysterectomies" caption="Examples of system maps created during our research.">
            <h2>Research & Systems Analysis</h2>
            <p>
              To understand the root causes of this crisis, our research involved extensive interviews with a diverse range of stakeholders: journalists, activists, healthcare experts, academics, and sugarcane laborers.
            </p>
            <p>
              We used systems mapping to visualize the web of interconnected factors, identifying hundreds of interrelated problems contributing to the crisis—from poor healthcare access and sanitation to exploitative labor practices and gender norms. This analysis confirmed our key insight: <b>no single "silver bullet" solution could effectively address a problem so deeply embedded in the region's socio-economic fabric.</b>
            </p>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/disha/field_guide_cover.webp" alt="Cover of the Field Guide for Systemic Action" caption="Cover of the Field Guide for Systemic Action.">
            <h2>Strategy: From Solution to Empowerment</h2>
            <p>
              The system's complexity demanded a strategic pivot. Instead of designing a single intervention, we shifted our focus to creating a tool that empowers local actors already working within the community. Our guiding question became: <b>"How can we equip someone inside the system to intervene effectively and ethically?"</b> This led to the development of a field guide aimed at building local capacity for systemic thinking and action.
            </p>
          </ProjectSideBySideReverse>

          <ProjectFullWidth image="/disha/field_guide_spread.webp" alt="Internal spread of the Field Guide showing frameworks" caption="Internal spread of the Field Guide.">
            <h2>Core Deliverable: A Field Guide for Systemic Action</h2>
            <p>
              The Field Guide is a comprehensive manual structured to guide users from understanding to action. It contains three key sections:
            </p>
            <ol>
              <li><b>Systemic Understanding:</b> This section uses detailed personas, stakeholder maps, and journey maps to illuminate the lived experiences of women and the multifaceted pressures that lead to a hysterectomy.</li>
              <li><b>Problems & Opportunities:</b> This section identifies leverage points across six critical domains (e.g., Healthcare Access, Economic Inequity, Gender Norms) and uses analytical frameworks to help users pinpoint systemic drivers.</li>
              <li><b>Designing Better Responses:</b> This section provides principles for systemic design, outlining an ethical, phased approach to creating, implementing, and sustaining interventions that minimize unintended harm and are tailored to local needs.</li>
            </ol>
          </ProjectFullWidth>

          <ProjectSideBySide image="/disha/interventions.webp" alt="Example interventions from the Field Guide" caption="Top to bottom: Maasik period tracking bangle; bagasse water purification teabag; Swasth Saathi Fellowship proposal.">
            <h2>Applied Concepts: Example Interventions</h2>
            <p>
              To demonstrate the practical application of the guide's frameworks, the manual includes several example interventions designed as starting points for community co-creation.
            </p>
            <ul>
              <li style={{ marginBottom: '1.5rem' }}>
                <b>Maasik Period Tracking Bangle:</b> A discreet wearable to help women track menstrual cycles, addressing issues of low menstrual literacy and social stigma.
                <div className="theory-of-change">
                  <span className="theory-of-change-label">Theory of Change:</span>
                  <span className="theory-of-change-flow">Increased menstrual literacy and preparation → fewer infections → reduced chronic pain → fewer hysterectomies sought for pain relief</span>
                </div>
              </li>
              <li style={{ marginBottom: '1.5rem' }}>
                <b>Sugarcane Bagasse Water Purification:</b> A low-cost water purification method using sugarcane waste (bagasse) to adsorb heavy metals from drinking water, addressing environmental risk factors for gynecological issues.
                <div className="theory-of-change">
                  <span className="theory-of-change-label">Theory of Change:</span>
                  <span className="theory-of-change-flow">Reduced exposure to harmful heavy metals → lower rates of uterine fibroids, endometriosis, cancer, and other gynecological issues  → fewer hysterectomies sought for medical reasons</span>
                </div>
              </li>
              <li>
                <b>Swasth Saathi Fellowship:</b> A program to train migrant women as in-camp health companions to bridge gaps in healthcare access, trust, and communication.
                <div className="theory-of-change">
                  <span className="theory-of-change-label">Theory of Change:</span>
                  <span className="theory-of-change-flow">Trusted peer support & guidance → women seek care earlier & make informed choices → better access to non-surgical treatments → fewer preventable hysterectomies</span>
                </div>
              </li>
            </ul>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/disha/organizations.webp" alt="Logos of local NGOs and UNDP" caption="Organizations we engaged with in India.">
            <h2>Validation & Co-Creation</h2>
            <p>
              The Field Guide and its concepts were developed and validated through active engagement with local NGOs, representatives from the UNDP, and migrant workers in India. This collaborative feedback was critical for refining the interventions. For instance, input from community members helped evolve the bagasse water purification concept into a user-friendly, teabag-like product better suited for resource-constrained camp settings.
            </p>
            <p>
              Please <a href="mailto:luke@lukefiorante.com" className="project-link">get in touch</a> for access to the Field Guide.
            </p>
          </ProjectSideBySideReverse>

        </div>
      </div>
      <ProjectFooter {...footerProps} />
    </div>
  );
}

export default DishaProjectDetail;
